import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import {
  fetchLiveTradingStatsSummary,
  fetchLiveTradingStatsTradeDetail,
  fetchLiveTradingStatsTrades,
} from '../../api/liveTradingStats';

const COLORS = {
  ink: '#17323c',
  muted: '#4f646f',
  success: '#2f7d5d',
  danger: '#9c4f4f',
  warning: '#b57422',
  blue: '#2f6f9f',
  line: 'rgba(23,50,60,0.12)',
  paper: '#ffffff',
  page: 'linear-gradient(180deg, #efe8dc 0%, #edf3f6 100%)',
};

const STATUS_OPTIONS = [
  ['', 'Tous'],
  ['INTENT_RECORDED', 'Intention créée'],
  ['SUBMITTING', 'Envoi en cours'],
  ['REJECTED', 'Rejeté'],
  ['FILL_UNKNOWN', 'Fill inconnu'],
  ['ACKNOWLEDGED', 'Acquitté sans fill'],
  ['PARTIALLY_FILLED', 'Entrée partielle'],
  ['FILLED', 'Exécuté'],
  ['POSITION_CLOSED', 'Position clôturée'],
];

const VIEW_OPTIONS = [
  ['ALL', 'Toutes les intentions'],
  ['OPEN', 'Positions ouvertes'],
  ['CLOSED', 'Trades clôturés'],
  ['NOT_OPENED', 'Intentions non exécutées'],
  ['EXECUTED', 'Trades exécutés'],
  ['UNKNOWN', 'Statut inconnu'],
];

const STATUS_COLORS = {
  OPEN: 'success',
  CLOSED: 'default',
  NOT_OPENED: 'warning',
  UNKNOWN: 'warning',
};

const DEFAULT_FILTERS = {
  exchange: 'BLOFIN',
  instrument: '',
  direction: '',
  status: 'FILLED',
  from: '',
  to: '',
  view: 'ALL',
};

const STATUS_VALUES = new Set(STATUS_OPTIONS.map(([value]) => value));
const VIEW_VALUES = new Set(VIEW_OPTIONS.map(([value]) => value));
const DIRECTION_VALUES = new Set(['LONG', 'SHORT']);

function copyDefaultFilters() {
  return { ...DEFAULT_FILTERS };
}

function queryValue(params, key, fallback, normalizer = (value) => value) {
  if (!params.has(key)) return fallback;
  return normalizer(params.get(key) || '');
}

function readInitialFilters() {
  if (typeof window === 'undefined' || !window.location) return copyDefaultFilters();
  const params = new URLSearchParams(window.location.search);
  const direction = queryValue(params, 'direction', DEFAULT_FILTERS.direction,
    (value) => value.trim().toUpperCase());
  const status = queryValue(params, 'status', DEFAULT_FILTERS.status,
    (value) => value.trim().toUpperCase());
  const view = queryValue(params, 'view', DEFAULT_FILTERS.view,
    (value) => value.trim().toUpperCase());
  return {
    exchange: queryValue(params, 'exchange', DEFAULT_FILTERS.exchange,
      (value) => value.trim().toUpperCase()),
    instrument: queryValue(params, 'instrument', DEFAULT_FILTERS.instrument,
      (value) => value.trim().toUpperCase()),
    direction: DIRECTION_VALUES.has(direction) ? direction : DEFAULT_FILTERS.direction,
    status: STATUS_VALUES.has(status) ? status : DEFAULT_FILTERS.status,
    from: queryValue(params, 'from', DEFAULT_FILTERS.from, (value) => value.slice(0, 10)),
    to: queryValue(params, 'to', DEFAULT_FILTERS.to, (value) => value.slice(0, 10)),
    view: VIEW_VALUES.has(view) ? view : DEFAULT_FILTERS.view,
  };
}

function syncFiltersToUrl(filters) {
  if (typeof window === 'undefined' || !window.history) return;
  const params = new URLSearchParams();
  const add = (key, value, defaultValue) => {
    if (value !== defaultValue || value === '') params.set(key, value ?? '');
  };
  add('exchange', filters.exchange, DEFAULT_FILTERS.exchange);
  add('instrument', filters.instrument, DEFAULT_FILTERS.instrument);
  add('direction', filters.direction, DEFAULT_FILTERS.direction);
  add('status', filters.status, DEFAULT_FILTERS.status);
  add('from', filters.from, DEFAULT_FILTERS.from);
  add('to', filters.to, DEFAULT_FILTERS.to);
  add('view', filters.view, DEFAULT_FILTERS.view);
  const query = params.toString();
  const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`;
  window.history.replaceState({}, '', nextUrl);
}

function rateText(rate) {
  if (!rate || !rate.available || rate.value === null || rate.value === undefined) return '—';
  const numeric = Number(rate.value);
  if (!Number.isFinite(numeric)) return '—';
  return `${(numeric * 100).toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
}

function ratioWidth(numerator, denominator) {
  const top = Number(numerator);
  const bottom = Number(denominator);
  if (!Number.isFinite(top) || !Number.isFinite(bottom) || bottom <= 0) return 0;
  return Math.max(0, Math.min(100, (top / bottom) * 100));
}

function directionStats(row, direction) {
  return (row?.directions || []).find((entry) => entry.direction === direction) || null;
}

function dateText(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
}

function timestampText(value) {
  if (value === null || value === undefined || value === '') return '—';
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp)) return '—';
  return dateText(new Date(timestamp).toISOString());
}

function amountText(money, fallback = '—') {
  if (!money || !money.available || money.amount === null || money.amount === undefined) return fallback;
  const numeric = Number(money.amount);
  if (!Number.isFinite(numeric)) return fallback;
  const text = numeric.toLocaleString('fr-FR', { maximumFractionDigits: 8 });
  return `${text}${money.currency ? ` ${money.currency}` : ''}`;
}

function countText(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString('fr-FR') : '0';
}

function countOrDash(value) {
  if (value === null || value === undefined) return '—';
  return countText(value);
}

function percentText(rate) {
  if (!rate || !rate.available) return '—';
  return `${(Number(rate.value) * 100).toFixed(1)} %`;
}

function MoneyWithReason({ money, fallback = '—' }) {
  if (!money || !money.available) {
    return (
      <Tooltip title={money?.reason || 'Donnée indisponible'}>
        <span tabIndex={0}>{fallback}</span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={money.complete ? 'Montant complet' : (money.reason || 'Montant partiel')}>
      <span tabIndex={0}>{amountText(money)}</span>
    </Tooltip>
  );
}

function SummaryCard({ label, value, hint, tone = 'default' }) {
  const color = tone === 'success' ? COLORS.success : tone === 'danger' ? COLORS.danger : COLORS.ink;
  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 0,
        p: 2,
        borderRadius: 4,
        bgcolor: COLORS.paper,
        border: `1px solid ${COLORS.line}`,
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: COLORS.muted, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.6 }}
      >
        {label}
      </Typography>
      <Typography
        variant="h6"
        title={typeof value === 'string' ? value : undefined}
        sx={{ color, fontWeight: 900, mt: 0.35, overflowWrap: 'anywhere', lineHeight: 1.2 }}
      >
        {value}
      </Typography>
      {hint ? <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mt: 0.2 }}>{hint}</Typography> : null}
    </Paper>
  );
}

function PnlHistoryChart({ points }) {
  if (!points?.length) {
    return <Alert severity="info">Aucun P&L connu sur des positions clôturées.</Alert>;
  }
  const values = points.map((point) => Number(point.cumulativeKnownGrossPnl));
  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  const span = max - min || 1;
  const width = 620;
  const height = 180;
  const padding = 20;
  const coords = points.map((point, index) => {
    const x = points.length === 1
      ? width / 2
      : padding + (index * (width - padding * 2)) / (points.length - 1);
    const y = height - padding - ((Number(point.cumulativeKnownGrossPnl) - min) / span) * (height - padding * 2);
    return `${x},${y}`;
  });
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box
        component="svg"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Évolution du P&L brut connu"
        sx={{ width: '100%', height: 'auto', maxHeight: 220, display: 'block' }}
      >
        <line x1={padding} x2={width - padding} y1={height - padding} y2={height - padding} stroke={COLORS.line} />
        <polyline fill="none" stroke={COLORS.ink} strokeWidth="3" points={coords.join(' ')} />
        {coords.map((coord, index) => {
          const [x, y] = coord.split(',').map(Number);
          return <circle key={`${points[index].at}-${index}`} cx={x} cy={y} r="4" fill={COLORS.ink} />;
        })}
      </Box>
      <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', mt: 1 }}>
        P&L brut connu, fills TP uniquement. Les frais d’entrée, le financement et les clôtures SL/manuelles ne sont pas inclus.
      </Typography>
    </Box>
  );
}

function TpFrequencyChart({ rows }) {
  if (!rows?.length) return <Alert severity="info">Aucune donnée TP disponible.</Alert>;
  return (
    <Stack spacing={1.5} sx={{ minWidth: 0 }}>
      {rows.map((row) => {
        const long = directionStats(row, 'LONG');
        const short = directionStats(row, 'SHORT');
        const totalWidth = ratioWidth(row.full, row.placed);
        const longWidth = ratioWidth(long?.full, row.placed);
        const shortWidth = ratioWidth(short?.full, row.placed);
        return (
          <Box key={row.level} data-testid={`tp-frequency-${row.level}`} sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 900, minWidth: 34 }}>{row.level}</Typography>
              <Box sx={{ flex: 1, minWidth: 45, height: 10, borderRadius: 99, bgcolor: '#e7eef5', overflow: 'hidden' }}>
                <Box
                  role="progressbar"
                  data-testid={`tp-total-bar-${row.level}`}
                  aria-label={`${row.level} total ${rateText(row.fullRate)}`}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={Math.round(totalWidth * 10) / 10}
                  sx={{ width: `${totalWidth}%`, height: '100%', bgcolor: COLORS.blue, borderRadius: 99 }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{ color: COLORS.blue, fontWeight: 700, textAlign: 'right', fontSize: { xs: '0.68rem', sm: '0.75rem' } }}
              >
                {`${countOrDash(row.full)} complets / ${countOrDash(row.placed)} placés · ${countOrDash(row.partial)} partiel(s) · ${rateText(row.fullRate)}`}
              </Typography>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ mt: 0.45, minWidth: 0, flexWrap: 'wrap' }}
            >
              <Typography
                variant="caption"
                sx={{ color: COLORS.success, fontWeight: 800, whiteSpace: 'nowrap', fontSize: { xs: '0.68rem', sm: '0.75rem' } }}
              >
                {`LONG ${countOrDash(long?.full)} complets / ${countOrDash(long?.placed)} placés · ${rateText(long?.fullRate)}`}
              </Typography>
              <Box
                role="img"
                data-testid={`tp-direction-bar-${row.level}`}
                aria-label={`${row.level} LONG ${countOrDash(long?.full)}/${countOrDash(long?.placed)} ${rateText(long?.fullRate)}, SHORT ${countOrDash(short?.full)}/${countOrDash(short?.placed)} ${rateText(short?.fullRate)}`}
                sx={{ flex: 1, minWidth: 60, height: 9, borderRadius: 99, bgcolor: '#e7eef5', overflow: 'hidden', display: 'flex' }}
              >
                <Box
                  data-testid={`tp-long-segment-${row.level}`}
                  sx={{ width: `${longWidth}%`, height: '100%', bgcolor: COLORS.success }}
                />
                <Box
                  data-testid={`tp-short-segment-${row.level}`}
                  sx={{ width: `${shortWidth}%`, height: '100%', bgcolor: COLORS.danger }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{ color: COLORS.danger, fontWeight: 800, whiteSpace: 'nowrap', fontSize: { xs: '0.68rem', sm: '0.75rem' } }}
              >
                {`SHORT ${countOrDash(short?.full)} complets / ${countOrDash(short?.placed)} placés · ${rateText(short?.fullRate)}`}
              </Typography>
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}

function BreakdownBars({ rows, emptyText }) {
  if (!rows?.length) return <Alert severity="info">{emptyText}</Alert>;
  const max = Math.max(1, ...rows.map((row) => Math.abs(Number(row.knownGrossPnl?.amount || 0))));
  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      {rows.map((row) => {
        const amount = Number(row.knownGrossPnl?.amount || 0);
        const width = (Math.abs(amount) / max) * 100;
        return (
          <Box key={row.key} sx={{ minWidth: 0 }}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.key}</Typography>
              <Typography variant="caption" sx={{ color: COLORS.muted }}>{row.trades} trade(s)</Typography>
            </Stack>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.3, minWidth: 0 }}>
              <Box sx={{ flex: 1, minWidth: 0, bgcolor: '#edf2f4', borderRadius: 99, height: 8, overflow: 'hidden' }}>
                <Box sx={{ width: `${width}%`, height: '100%', bgcolor: amount < 0 ? COLORS.danger : COLORS.success }} />
              </Box>
              <Typography
                variant="caption"
                sx={{ minWidth: 90, textAlign: 'right', color: amount < 0 ? COLORS.danger : COLORS.success }}
              >
                {amount === 0 ? '—' : amount.toLocaleString('fr-FR', { maximumFractionDigits: 8 })}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

function TpCell({ leg }) {
  if (!leg || (!leg.placed && !leg.targetPrice)) return <span>—</span>;
  const label = leg.full ? 'E' : leg.partial ? 'P' : leg.placed ? 'Placé' : 'Prévu';
  const color = leg.full ? 'success' : leg.partial ? 'warning' : 'default';
  return (
    <Tooltip
      title={`${leg.level} · cible ${leg.targetPrice ?? '—'} · exécuté ${leg.filledContracts ?? 0}/${leg.plannedContracts ?? '—'} · état ${leg.orderState || '—'}`}
    >
      <Stack spacing={0.25} alignItems="flex-start" tabIndex={0}>
        <Chip size="small" color={color} label={label} sx={{ height: 20, fontWeight: 700 }} />
        <Typography variant="caption" sx={{ color: COLORS.muted }}>
          {leg.filledContracts ?? 0}/{leg.plannedContracts ?? '—'}
        </Typography>
      </Stack>
    </Tooltip>
  );
}

const EXIT_INDICATORS = {
  S: {
    label: 'Stop-loss initial atteint',
    color: 'error',
    description: 'La position a été clôturée par le stop-loss initial.',
  },
  L: {
    label: 'Perte avant le niveau d’entrée',
    color: 'error',
    description: 'La sortie est défavorable par rapport au prix d’entrée, hors stop-loss initial.',
  },
  B: {
    label: 'Break-even',
    color: 'warning',
    description: 'Le prix de sortie est égal au prix d’entrée.',
  },
  G: {
    label: 'Gain entre l’entrée et TP1',
    color: 'success',
    description: 'La sortie est favorable mais reste avant TP1.',
  },
  T: {
    label: 'Gain au niveau ou au-delà de TP1',
    color: 'success',
    description: 'La sortie est favorable au niveau de TP1 ou au-delà ; les lettres TP restent visibles.',
  },
  '?': {
    label: 'Sortie indéterminée',
    color: 'default',
    description: 'La cause ou le prix exécuté de la sortie n’est pas suffisamment persisté.',
  },
};

const EXIT_REASONS = {
  EXIT_PRICE_NOT_PERSISTED: 'prix de sortie non persisté',
  EXIT_QUANTITY_NOT_PERSISTED: 'quantité de sortie non persistée',
  EXIT_COVERAGE_INCOMPLETE: 'couverture de sortie incomplète',
  EXIT_COVERAGE_AMBIGUOUS: 'couverture de sortie ambiguë',
  EXIT_CAUSE_AND_PRICE_UNKNOWN: 'cause et prix de sortie indisponibles',
};

const EXIT_SOURCES = {
  STOP_LOSS_INITIAL: 'stop-loss initial',
  TP_FILLS: 'fills TP',
  EXIT_FILLS: 'fills de clôture',
  EXECUTION_EVENT: 'événement d’exécution',
  UNKNOWN: 'source inconnue',
};

function exitTitle(exit) {
  if (!exit) return 'Aucune sortie à afficher.';
  const info = EXIT_INDICATORS[exit.code] || EXIT_INDICATORS['?'];
  const details = [info.description];
  if (exit.exitPrice !== null && exit.exitPrice !== undefined) {
    details.push(`Prix exécuté : ${exit.exitPrice}`);
  }
  if (exit.exitQuantity !== null && exit.exitQuantity !== undefined) {
    details.push(`Quantité sortie : ${exit.exitQuantity}`);
  }
  if (exit.coveredQuantity !== null && exit.coveredQuantity !== undefined) {
    details.push(`Quantité couverte : ${exit.coveredQuantity}`);
  }
  if (exit.fillCount !== null && exit.fillCount !== undefined) {
    details.push(`Fills de clôture : ${exit.fillCount}`);
  }
  if (exit.exitTs !== null && exit.exitTs !== undefined) {
    details.push(`Horodatage exécuté : ${timestampText(exit.exitTs)}`);
  }
  if (exit.source) {
    details.push(`Source : ${EXIT_SOURCES[exit.source] || exit.source}`);
  }
  if (exit.partial) details.push('Sortie partielle');
  if (exit.reason) details.push(EXIT_REASONS[exit.reason] || exit.reason);
  return details.join(' · ');
}

function ExitChip({ exit, testId, prefix = '' }) {
  if (!exit) return <span>—</span>;
  const info = EXIT_INDICATORS[exit.code] || EXIT_INDICATORS['?'];
  const label = `${prefix}${exit.code || '?'}${exit.partial ? '*' : ''}`;
  return (
    <Tooltip title={exitTitle(exit)}>
      <Chip
        size="small"
        color={info.color}
        label={label}
        data-testid={testId}
        aria-label={`${label} — ${info.label}. ${exitTitle(exit)}`}
        sx={{ height: 20, fontWeight: 800 }}
      />
    </Tooltip>
  );
}

function ExitCell({ exit, intentId }) {
  if (!exit) return <span>—</span>;
  return (
    <Stack spacing={0.25} alignItems="flex-start">
      <ExitChip exit={exit} testId={`exit-indicator-${intentId}`} />
      <Typography variant="caption" sx={{ color: COLORS.muted }}>
        {exit.partial ? 'partiel' : exit.finalExit ? 'final' : 'indéterminé'}
      </Typography>
    </Stack>
  );
}

function ExitLegend() {
  return (
    <Stack
      data-testid="exit-legend"
      direction={{ xs: 'column', md: 'row' }}
      spacing={0.75}
      alignItems={{ md: 'center' }}
      useFlexGap
      flexWrap="wrap"
      sx={{ mb: 1.25 }}
    >
      <Typography variant="caption" sx={{ color: COLORS.muted, fontWeight: 800 }}>
        Légende sorties
      </Typography>
      <Chip size="small" color="success" label="TP E = TP complet" sx={{ height: 20 }} />
      <Chip size="small" color="warning" label="TP P = TP partiel" sx={{ height: 20 }} />
      {Object.entries(EXIT_INDICATORS).map(([code, info]) => (
        <Tooltip key={code} title={info.description}>
          <Chip size="small" color={info.color} label={`${code} = ${info.label}`} sx={{ height: 20 }} />
        </Tooltip>
      ))}
      <Typography variant="caption" sx={{ color: COLORS.muted }}>
        * sortie partielle · P reste réservé au TP partiel · L évite cette ambiguïté.
        Les codes de prix exigent un prix de sortie exécuté persisté ; ? est affiché sinon.
      </Typography>
    </Stack>
  );
}

function TradeTable({ rows, onDetail, loading }) {
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>;
  }
  if (!rows?.length) {
    return <Alert severity="info">Aucun trade LIVE pour ces filtres.</Alert>;
  }
  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ display: 'block', width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'auto', borderRadius: 3 }}
    >
      <Table size="small" sx={{ minWidth: 1450 }}>
        <TableHead>
          <TableRow>
            {['Ouverture', 'Exchange', 'Instrument', 'Direction', 'Qté', 'Entrée', 'SL initial', 'Dernier SL',
              'TP1', 'TP2', 'TP3', 'Restant', 'Sortie', 'Statut actuel', 'P&L brut TP', 'Net', 'Frais connus', 'Clôture', ''].map((label) => (
              <TableCell key={label} sx={{ fontWeight: 800, bgcolor: '#f7fafb', whiteSpace: 'nowrap' }}>{label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const byLevel = Object.fromEntries((row.takeProfits || []).map((leg) => [leg.level, leg]));
            return (
              <TableRow key={row.intentId} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateText(row.openedAt)}</TableCell>
                <TableCell>{row.exchange}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{row.instrument}</TableCell>
                <TableCell>
                  <Chip size="small" label={row.direction} color={row.direction === 'LONG' ? 'success' : 'error'} />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.filledContracts ?? '—'} / {row.requestedContracts ?? '—'}</TableCell>
                <TableCell>{row.averageEntryPrice ?? '—'}</TableCell>
                <TableCell>{row.initialStopLoss ?? '—'}</TableCell>
                <TableCell>{row.lastStopLoss ?? '—'}</TableCell>
                <TableCell><TpCell leg={byLevel.TP1} /></TableCell>
                <TableCell><TpCell leg={byLevel.TP2} /></TableCell>
                <TableCell><TpCell leg={byLevel.TP3} /></TableCell>
                <TableCell>{row.remainingContracts ?? '—'}</TableCell>
                <TableCell><ExitCell exit={row.exit} intentId={row.intentId} /></TableCell>
                <TableCell>
                  <Tooltip title={`${row.positionReason || row.positionStatus} · lecture ${dateText(row.positionReadAt)}`}>
                    <Chip size="small" label={row.positionStatus} color={STATUS_COLORS[row.positionStatus] || 'default'} />
                  </Tooltip>
                </TableCell>
                <TableCell><MoneyWithReason money={row.knownGrossPnl} /></TableCell>
                <TableCell><MoneyWithReason money={row.netPnl} /></TableCell>
                <TableCell><MoneyWithReason money={row.knownFees} /></TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateText(row.closedAt)}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => onDetail(row.intentId)}>Détails</Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function MobileCards({ rows, onDetail }) {
  if (!rows?.length) return <Alert severity="info">Aucun trade LIVE pour ces filtres.</Alert>;
  return (
    <Stack spacing={1.25}>
      {rows.map((row) => (
        <Card key={row.intentId} variant="outlined" sx={{ borderRadius: 3, minWidth: 0 }}>
          <CardContent sx={{ minWidth: 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900, overflowWrap: 'anywhere' }}>{row.instrument}</Typography>
                <Typography variant="caption" sx={{ color: COLORS.muted }}>{dateText(row.openedAt)} · {row.exchange}</Typography>
              </Box>
              <Chip size="small" label={row.direction} color={row.direction === 'LONG' ? 'success' : 'error'} />
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              <Chip size="small" label={row.positionStatus} color={STATUS_COLORS[row.positionStatus] || 'default'} />
              <Chip size="small" label={`Entrée ${row.averageEntryPrice ?? '—'}`} />
              <Chip size="small" label={`Qté ${row.filledContracts ?? '—'}`} />
              <Chip size="small" label={`TP1 ${row.takeProfits?.find((leg) => leg.level === 'TP1')?.full ? 'E' : '—'}`} />
              <Chip size="small" label={`TP2 ${row.takeProfits?.find((leg) => leg.level === 'TP2')?.full ? 'E' : '—'}`} />
              <Chip size="small" label={`TP3 ${row.takeProfits?.find((leg) => leg.level === 'TP3')?.full ? 'E' : '—'}`} />
              {row.exit
                ? <ExitChip exit={row.exit} testId={`exit-mobile-${row.intentId}`} prefix="Sortie " />
                : <Chip size="small" label="Sortie —" />}
            </Stack>
            <Typography variant="body2" sx={{ mt: 1, overflowWrap: 'anywhere' }}>
              P&L brut : <MoneyWithReason money={row.knownGrossPnl} />
            </Typography>
            <Typography variant="caption" sx={{ color: COLORS.muted, display: 'block', overflowWrap: 'anywhere' }}>
              Net : <MoneyWithReason money={row.netPnl} /> · Frais : <MoneyWithReason money={row.knownFees} />
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Button size="small" onClick={() => onDetail(row.intentId)}>Détails</Button>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );
}

function TradeDetailDialog({ open, detail, loading, error, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>Détail du trade LIVE</DialogTitle>
      <DialogContent dividers>
        {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
        {detail ? (
          <Stack spacing={2} sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={detail.trade.instrument} color="primary" />
              <Chip label={detail.trade.direction} color={detail.trade.direction === 'LONG' ? 'success' : 'error'} />
              <Chip label={detail.trade.positionStatus} />
              <Chip label={`Ouvert ${dateText(detail.trade.openedAt)}`} variant="outlined" />
              <Chip label={`Clôturé ${dateText(detail.trade.closedAt)}`} variant="outlined" />
              <Chip label={`Clôture: ${detail.trade.closureReason || '—'}`} variant="outlined" />
              {detail.trade.exit ? <ExitChip exit={detail.trade.exit} prefix="Sortie " /> : null}
            </Stack>
            <Alert severity="warning">
              P&L brut partiel calculé uniquement sur les fills TP persistés. Frais d’entrée, funding,
              SL et clôtures manuelles ne sont pas persistés dans ce périmètre.
            </Alert>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>TP</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    {['Niveau', 'Cible', 'Prévu', 'Placé', 'État', 'Exécuté', 'Prix moyen', 'Frais', 'Statut'].map((label) => (
                      <TableCell key={label} sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.takeProfits.map((leg) => (
                    <TableRow key={leg.level}>
                      <TableCell>{leg.level}</TableCell>
                      <TableCell>{leg.targetPrice ?? '—'}</TableCell>
                      <TableCell>{leg.plannedContracts ?? '—'}</TableCell>
                      <TableCell>{leg.placed ? 'Oui' : 'Non'}</TableCell>
                      <TableCell>{leg.orderState || '—'}</TableCell>
                      <TableCell>{leg.filledContracts ?? '—'}</TableCell>
                      <TableCell>{leg.averageFillPrice ?? '—'}</TableCell>
                      <TableCell><MoneyWithReason money={leg.knownFees} /></TableCell>
                      <TableCell>{leg.full ? 'Complet' : leg.partial ? 'Partiel' : 'Non exécuté'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Fills</Typography>
            {detail.fills.length ? (
              <TableContainer component={Paper} variant="outlined" sx={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      {['Niveau', 'Order', 'Trade', 'Prix', 'Taille', 'Frais', 'Timestamp'].map((label) => (
                        <TableCell key={label} sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.fills.map((fill) => (
                      <TableRow key={`${fill.orderId}-${fill.tradeId}`}>
                        <TableCell>{fill.level}</TableCell>
                        <TableCell>{fill.orderId}</TableCell>
                        <TableCell>{fill.tradeId}</TableCell>
                        <TableCell>{fill.fillPrice}</TableCell>
                        <TableCell>{fill.fillSize}</TableCell>
                        <TableCell>{fill.fee ?? '—'}</TableCell>
                        <TableCell>{timestampText(fill.fillTs)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : <Alert severity="info">Aucun fill TP persisté.</Alert>}
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Chronologie</Typography>
            {detail.events.length ? (
              <Stack spacing={1}>
                {detail.events.map((event) => (
                  <Stack key={event.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ minWidth: 0 }}>
                    <Typography variant="caption" sx={{ color: COLORS.muted, minWidth: 150 }}>{dateText(event.createdAt)}</Typography>
                    <Chip size="small" label={event.eventType} />
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{event.detail || '—'}</Typography>
                  </Stack>
                ))}
              </Stack>
            ) : <Alert severity="info">Aucun événement persisté.</Alert>}
          </Stack>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default function TradingStatsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [draft, setDraft] = useState(readInitialFilters);
  const [filters, setFilters] = useState(readInitialFilters);
  const [summary, setSummary] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const requestFilters = useMemo(() => ({
    exchange: filters.exchange || undefined,
    instrument: filters.instrument || undefined,
    direction: filters.direction || undefined,
    status: filters.status || undefined,
    from: filters.from ? `${filters.from}T00:00:00` : undefined,
    to: filters.to ? `${filters.to}T23:59:59` : undefined,
    view: filters.view || 'ALL',
  }), [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [nextSummary, nextPage] = await Promise.all([
        fetchLiveTradingStatsSummary(requestFilters),
        fetchLiveTradingStatsTrades({ ...requestFilters, page, size: 25 }),
      ]);
      setSummary(nextSummary);
      setPageData(nextPage);
    } catch (loadError) {
      setError(loadError.message || 'Impossible de charger les statistiques LIVE.');
      setSummary(null);
      setPageData(null);
    } finally {
      setLoading(false);
    }
  }, [page, requestFilters]);

  useEffect(() => { load(); }, [load]);

  const applyFilters = () => {
    const next = { ...draft };
    setPage(0);
    setFilters(next);
    syncFiltersToUrl(next);
  };

  const resetFilters = () => {
    const next = copyDefaultFilters();
    setDraft(next);
    setFilters(next);
    setPage(0);
    syncFiltersToUrl(next);
  };

  const openDetail = async (intentId) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailError('');
    setDetailLoading(true);
    try {
      setDetail(await fetchLiveTradingStatsTradeDetail(intentId));
    } catch (loadError) {
      setDetailError(loadError.message || 'Détail indisponible.');
    } finally {
      setDetailLoading(false);
    }
  };

  const rows = pageData?.rows || [];
  const totalPages = pageData?.totalPages || 0;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        '&, & *': { boxSizing: 'border-box' },
        p: { xs: 1.5, md: 3 },
        background: COLORS.page,
        minHeight: '100%',
      }}
    >
      <Stack spacing={{ xs: 1.5, md: 2.5 }} sx={{ width: '100%', maxWidth: 1660, mx: 'auto', minWidth: 0 }}>
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            minWidth: 0,
            p: { xs: 2, md: 3 },
            borderRadius: 5,
            background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)',
            color: '#f7f3ea',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5} alignItems={{ md: 'center' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h4" sx={{ fontWeight: 900, overflowWrap: 'anywhere' }}>
                Statistiques trading LIVE
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(247,243,234,0.75)' }}>
                Lecture seule des intentions, fills TP et états persistés. L’historique est filtré
                après le 21/10/2026 18:20 (Europe/Paris) ; les positions encore ouvertes restent
                visibles pour le suivi opérationnel. Aucun appel exchange d’écriture.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignSelf: { xs: 'stretch', md: 'auto' } }}>
              <Button
                href="/trading/orders"
                variant="outlined"
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: '#fff' }, whiteSpace: 'nowrap' }}
              >
                Ordres crypto
              </Button>
              <Button
                startIcon={<RefreshRoundedIcon />}
                variant="outlined"
                onClick={load}
                disabled={loading}
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: '#fff' }, alignSelf: { xs: 'flex-start', md: 'auto' } }}
              >
                Actualiser
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: 2, borderRadius: 5, bgcolor: COLORS.paper, border: `1px solid ${COLORS.line}`, minWidth: 0 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} useFlexGap flexWrap="wrap" alignItems={{ md: 'center' }}>
            <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '1 1 150px' }, minWidth: 0 }}>
              <InputLabel>Exchange</InputLabel>
              <Select label="Exchange" value={draft.exchange} onChange={(event) => setDraft({ ...draft, exchange: event.target.value })} inputProps={{ 'aria-label': 'Exchange' }}>
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="BLOFIN">BloFin</MenuItem>
                <MenuItem value="BITUNIX">Bitunix</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Instrument"
              value={draft.instrument}
              onChange={(event) => setDraft({ ...draft, instrument: event.target.value })}
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 180px' }, minWidth: 0 }}
            />
            <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '1 1 140px' }, minWidth: 0 }}>
              <InputLabel>Direction</InputLabel>
              <Select label="Direction" value={draft.direction} onChange={(event) => setDraft({ ...draft, direction: event.target.value })} inputProps={{ 'aria-label': 'Direction' }}>
                <MenuItem value="">Toutes</MenuItem>
                <MenuItem value="LONG">LONG</MenuItem>
                <MenuItem value="SHORT">SHORT</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: 0 }}>
              <InputLabel>Vue</InputLabel>
              <Select
                label="Vue"
                value={draft.view}
                onChange={(event) => setDraft({ ...draft, view: event.target.value })}
                inputProps={{ 'aria-label': 'Vue' }}
              >
                {VIEW_OPTIONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '1 1 190px' }, minWidth: 0 }}>
              <InputLabel>Statut local</InputLabel>
              <Select label="Statut local" value={draft.status} onChange={(event) => setDraft({ ...draft, status: event.target.value })} inputProps={{ 'aria-label': 'Statut local' }}>
                {STATUS_OPTIONS.map(([value, label]) => <MenuItem key={value || 'all'} value={value}>{label}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="date"
              label="Du"
              value={draft.from}
              onChange={(event) => setDraft({ ...draft, from: event.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 150px' }, minWidth: 0 }}
            />
            <TextField
              size="small"
              type="date"
              label="Au"
              value={draft.to}
              onChange={(event) => setDraft({ ...draft, to: event.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: { xs: '1 1 100%', sm: '1 1 150px' }, minWidth: 0 }}
            />
            <Stack direction="row" spacing={1} sx={{ flex: { xs: '1 1 100%', md: '0 0 auto' } }}>
              <Button variant="contained" onClick={applyFilters} sx={{ bgcolor: COLORS.ink, '&:hover': { bgcolor: '#244b55' } }}>Filtrer</Button>
              <Button onClick={resetFilters}>Réinitialiser</Button>
            </Stack>
          </Stack>
        </Paper>

        {error ? <Alert severity="error">{error}</Alert> : null}
        {summary ? (
          <Alert
            severity={summary.positionSync?.state === 'SUCCESS' ? 'info' : 'warning'}
            icon={false}
            sx={{ minWidth: 0 }}
          >
            {summary.positionSync?.state === 'SUCCESS'
              ? `Positions synchronisées en lecture seule depuis les exchanges à ${dateText(summary.positionSync.readAt)}. `
              : `Synchronisation des positions indisponible (${summary.positionSync?.reason || 'raison inconnue'}). Les statuts OPEN/CLOSED ne sont pas affirmés. `}
            Le P&L affiché reste un P&L brut partiel fondé sur les fills TP persistés. Frais d’entrée, funding,
            SL et clôtures manuelles ne sont pas encore persistés : le P&L net et le win rate restent indisponibles.
          </Alert>
        ) : null}

        {summary ? (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, minmax(0, 1fr))',
                  sm: 'repeat(3, minmax(0, 1fr))',
                  md: 'repeat(4, minmax(0, 1fr))',
                  xl: 'repeat(6, minmax(0, 1fr))',
                },
                gap: 1.25,
                minWidth: 0,
              }}
            >
              <SummaryCard label="Intentions" value={countText(summary.intentionsCreated)} hint="Historique conservé" />
              <SummaryCard label="Trades exécutés" value={countText(summary.executedPositions ?? summary.positionsOpened)} hint={`${countText(summary.notOpenedPositions)} non exécutée(s)`} />
              <SummaryCard label="Positions ouvertes" value={countOrDash(summary.openPositions)} hint="Lecture exchange" tone="success" />
              <SummaryCard label="Trades clôturés" value={countOrDash(summary.closedPositions)} hint="Positions distantes absentes" />
              <SummaryCard label="Statut inconnu" value={countText(summary.unknownPositions)} hint="Synchronisation insuffisante" />
              <SummaryCard label="P&L brut TP" value={amountText(summary.knownGrossPnl)} hint="Partiel, fills TP seulement" tone="success" />
              <SummaryCard label="P&L total réalisé" value={amountText(summary.totalRealizedPnl)} hint="Sorties complètes requises" tone="danger" />
              <SummaryCard label="P&L net" value={amountText(summary.netPnl)} hint="Indisponible" tone="danger" />
              <SummaryCard label="Frais connus" value={amountText(summary.knownFees)} hint="Partiels, hors frais d’entrée" />
              <SummaryCard label="Win rate" value={percentText(summary.winRate)} hint="P&L complet requis" />
              <SummaryCard label="Gain moyen" value={amountText(summary.averageWin)} />
              <SummaryCard label="Perte moyenne" value={amountText(summary.averageLoss)} />
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)' },
                gap: 1.5,
                minWidth: 0,
              }}
            >
              <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 5, bgcolor: COLORS.paper, border: `1px solid ${COLORS.line}`, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>Évolution du P&L brut connu</Typography>
                <PnlHistoryChart points={summary.realizedPnlHistory} />
              </Paper>
              <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 5, bgcolor: COLORS.paper, border: `1px solid ${COLORS.line}`, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>Fréquence TP</Typography>
                <TpFrequencyChart rows={summary.takeProfits || []} />
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="caption" sx={{ color: COLORS.muted }}>
                  Gagnants/perdants connus : {summary.winLossDistribution?.winners ?? 0}/{summary.winLossDistribution?.losers ?? 0}
                  {summary.winLossDistribution?.complete ? '' : ' (base partielle)'}
                </Typography>
              </Paper>
              <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 5, bgcolor: COLORS.paper, border: `1px solid ${COLORS.line}`, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>LONG / SHORT</Typography>
                <BreakdownBars rows={summary.byDirection || []} emptyText="Aucun résultat." />
              </Paper>
            </Box>

            <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 5, bgcolor: COLORS.paper, border: `1px solid ${COLORS.line}`, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>Performance par instrument</Typography>
              <BreakdownBars rows={summary.byInstrument || []} emptyText="Aucun instrument." />
            </Paper>
          </>
        ) : null}

        <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 5, bgcolor: COLORS.paper, border: `1px solid ${COLORS.line}`, minWidth: 0 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={0.5} sx={{ mb: 1 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>Historique des intentions et des trades</Typography>
              <Typography variant="caption" sx={{ color: COLORS.muted }}>
                Historique filtré après le cutoff du 21/10/2026 18:20 Europe/Paris.
                Les positions encore ouvertes sont conservées pour le suivi opérationnel.
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: COLORS.muted }}>{pageData ? `${pageData.totalElements} ligne(s)` : ''}</Typography>
          </Stack>
          <ExitLegend />
          {isMobile ? <MobileCards rows={rows} onDetail={openDetail} /> : <TradeTable rows={rows} onDetail={openDetail} loading={loading} />}
          <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" sx={{ mt: 1.5, flexWrap: 'wrap' }}>
            <Button disabled={page <= 0 || loading} onClick={() => setPage((current) => Math.max(0, current - 1))}>Précédent</Button>
            <Typography variant="caption" sx={{ color: COLORS.muted }}>
              Page {totalPages === 0 ? 0 : page + 1} / {totalPages}
            </Typography>
            <Button disabled={loading || page + 1 >= totalPages} onClick={() => setPage((current) => current + 1)}>Suivant</Button>
          </Stack>
        </Paper>
      </Stack>

      <TradeDetailDialog
        open={detailOpen}
        detail={detail}
        loading={detailLoading}
        error={detailError}
        onClose={() => setDetailOpen(false)}
      />
    </Box>
  );
}
